import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useQueries } from '@ltvco/refresh-lib/vendors';
import { useContext, useMemo } from 'react';

import { fdaRecalls, usdaRecalls, cpscRecalls } from '../api';

import {
  CPSC_RECALLS_QUERY_KEY_PREFIX,
  FDA_RECALLS_QUERY_KEY_PREFIX,
  USDA_RECALLS_QUERY_KEY_PREFIX,
  USDA_TRANSLATION_LANGUAGE_IDS,
} from './constants';
import { buildAllSortedByDate, sortFdaByInitiationDate } from './utils';

import type { FdaEnforcementSearchParams, RecallItem } from '../interfaces';

const LIMIT = 100;

const FDA_NEWEST_SEARCH_PARAMS: FdaEnforcementSearchParams = {
  limit: LIMIT,
  sort: 'report_date:desc',
};

/**
 * React Query hook for newest recalls across all endpoints (FDA drug/device/food, USDA, CPSC).
 * Uses useQueries for parallel fetching with shared cache keys.
 * Returns one list sorted by recall date (newest first).
 */
export const useNewestRecalls = (options?: { enabled?: boolean }) => {
  const { logError } = useContext(AppConfig);
  const enabled = options?.enabled !== false;

  const results = useQueries({
    queries: [
      {
        queryKey: [
          FDA_RECALLS_QUERY_KEY_PREFIX,
          'drug',
          FDA_NEWEST_SEARCH_PARAMS,
        ],
        queryFn: () =>
          fdaRecalls.fetchEnforcement({
            domain: 'drug',
            searchParams: FDA_NEWEST_SEARCH_PARAMS,
          }),
        enabled,
        refetchOnWindowFocus: false,
        onError: (error: unknown) =>
          logError(`${FDA_RECALLS_QUERY_KEY_PREFIX} failed`, error as Error),
      },
      {
        queryKey: [
          FDA_RECALLS_QUERY_KEY_PREFIX,
          'device',
          FDA_NEWEST_SEARCH_PARAMS,
        ],
        queryFn: () =>
          fdaRecalls.fetchEnforcement({
            domain: 'device',
            searchParams: FDA_NEWEST_SEARCH_PARAMS,
          }),
        enabled,
        refetchOnWindowFocus: false,
        onError: (error: unknown) =>
          logError(`${FDA_RECALLS_QUERY_KEY_PREFIX} failed`, error as Error),
      },
      {
        queryKey: [
          FDA_RECALLS_QUERY_KEY_PREFIX,
          'food',
          FDA_NEWEST_SEARCH_PARAMS,
        ],
        queryFn: () =>
          fdaRecalls.fetchEnforcement({
            domain: 'food',
            searchParams: FDA_NEWEST_SEARCH_PARAMS,
          }),
        enabled,
        refetchOnWindowFocus: false,
        onError: (error: unknown) =>
          logError(`${FDA_RECALLS_QUERY_KEY_PREFIX} failed`, error as Error),
      },
      {
        queryKey: [
          USDA_RECALLS_QUERY_KEY_PREFIX,
          { field_translation_language: USDA_TRANSLATION_LANGUAGE_IDS.English },
        ],
        queryFn: () =>
          usdaRecalls.fetchRecalls({
            field_translation_language: USDA_TRANSLATION_LANGUAGE_IDS.English,
          }),
        enabled,
        refetchOnWindowFocus: false,
        onError: (error: unknown) =>
          logError(`${USDA_RECALLS_QUERY_KEY_PREFIX} failed`, error as Error),
      },
      {
        queryKey: [CPSC_RECALLS_QUERY_KEY_PREFIX, null],
        queryFn: () => cpscRecalls.fetchRecalls(),
        enabled,
        refetchOnWindowFocus: false,
        onError: (error: unknown) =>
          logError(`${CPSC_RECALLS_QUERY_KEY_PREFIX} failed`, error as Error),
      },
    ],
  });

  const [fdaDrug, fdaDevice, fdaFood, usda, cpsc] = results;

  const data = useMemo((): RecallItem[] | undefined => {
    const drug = sortFdaByInitiationDate(fdaDrug.data?.results ?? []);
    const device = sortFdaByInitiationDate(fdaDevice.data?.results ?? []);
    const food = sortFdaByInitiationDate(fdaFood.data?.results ?? []);
    const usdaList = (usda.data ?? []).slice(0, LIMIT);
    const cpscList = (cpsc.data ?? []).slice(0, LIMIT);
    return buildAllSortedByDate(drug, device, food, usdaList, cpscList, []);
  }, [fdaDrug.data, fdaDevice.data, fdaFood.data, usda.data, cpsc.data]);

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  const error = results.find((r) => r.error)?.error ?? null;
  const isFetching = results.some((r) => r.isFetching);

  return { data, isLoading, isError, error, isFetching };
};
